import React, { useState, useEffect, useRef, useCallback } from "react";
import { Table, Button, Form, Tabs, Tab, Row, Col } from "react-bootstrap";
import Header from "./header";
import "../App.css";
import { useNavigate } from "react-router-dom";

function Orders() {
  const navigate = useNavigate();
  const backendURI = process.env.REACT_APP_BK_URL;
  const searchTimeoutRef = useRef(null);

  const [data, setData] = useState([]);
  const [message, setMessage] = useState("");
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(100);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const [conditions, setConditions] = useState({
    process: false,
    completed: false,
    paid: false,
    paid_change_class_change: "",
    username: JSON.parse(localStorage.getItem("username")),
    shopname: JSON.parse(localStorage.getItem("address")),
    id: JSON.parse(localStorage.getItem("id")),
    home_delivery: false,
    pk_stat: false,
    order_id: undefined,
  });

  const loadOrders = useCallback(
    async (targetPage = 1, append = false) => {
      try {
        setLoading(true);
        setMessage("");

        const response = await fetch(
          `${backendURI}/api/ordersadmin/${conditions.id}/${conditions.home_delivery}?page=${targetPage}&limit=${limit}`
        );
        const json = await response.json();

        if (json.dbError !== undefined) {
          setMessage("Failed to load orders");
          if (!append) setData([]);
          setHasMore(false);
          return;
        }

        const items = Array.isArray(json.items) ? json.items : [];

        setData((prev) => (append ? [...prev, ...items] : items));
        setHasMore(Boolean(json.hasMore));

        if (!items.length && !append) {
          setMessage(
            "No order added for this admin. Add orders by clicking on Add New Order +"
          );
        }
      } catch (err) {
        setMessage("Failed to load orders");
        if (!append) setData([]);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [backendURI, conditions.id, conditions.home_delivery, limit]
  );

  const refreshOrders = useCallback(() => {
    setIsSearching(false);
    setSearchText("");
    setPage(1);
    loadOrders(1, false);
  }, [loadOrders]);

  useEffect(() => {
    setPage(1);
    setIsSearching(false);
    setSearchText("");
    loadOrders(1, false);
  }, [conditions.id, conditions.home_delivery, loadOrders]);

  useEffect(() => {
    fetch(`${backendURI}/api/pickup/pkstat/${conditions.username}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
      },
    })
      .then((res) => res.json())
      .then((json) => {
        setConditions((prev) => ({
          ...prev,
          pk_stat: json?.[0]?.ans ?? false,
        }));
      })
      .catch(() => { });
  }, [conditions.username, backendURI]);

  const handleDelete = async (id) => {
    try {
      await fetch(`${backendURI}/api/orders/${id}`, {
        method: "DELETE",
      });
      alert(`The order with order id ${id} has been deleted.`);
      refreshOrders();
    } catch (err) {
      alert("Failed to delete order");
    }
  };

  const completeOrder = async (id, customerName) => {
    try {
      const res = await fetch(`${backendURI}/api/users/findone/${customerName}`);
      const customerInfo = await res.json();
      const customerPhone = customerInfo?.[0]?.phone;

      await fetch(`${backendURI}/api/orders/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json;charset=UTF-8",
        },
        body: JSON.stringify({
          completed: true,
          customer_phone: customerPhone,
        }),
      });

      alert("Order is completed");
      refreshOrders();
    } catch (err) {
      alert("Failed to complete order");
    }
  };

  const handleChange = async (e, id, customerName) => {
    try {
      if (e.target.value === "process" && e.target.checked === true) {
        await fetch(`${backendURI}/api/orders/${id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json;charset=UTF-8",
          },
          body: JSON.stringify({ process: true }),
        });
        alert("Order is processing");
        refreshOrders();
      } else if (
        e.target.value === "completed" &&
        e.target.checked === true
      ) {
        await completeOrder(id, customerName);
      } else if (e.target.value === "paid" && e.target.checked === true) {
        await fetch(`${backendURI}/api/orders/${id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json;charset=UTF-8",
          },
          body: JSON.stringify({ paid: true }),
        });
        alert("Order is paid for");
        refreshOrders();
      }
    } catch (err) {
      alert("Failed to update order");
    }
  };

  const handleHd = (e) => {
    const ans = e.target.checked;

    fetch(`${backendURI}/api/pickup/stat/`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
      },
      body: JSON.stringify({
        admin: conditions.username,
        ans: ans,
      }),
    })
      .then((res) => res.json())
      .then((json) => {
        setConditions((prev) => ({ ...prev, pk_stat: json?.[0]?.ans ?? ans }));
        alert(`The delivery is now: ${ans}`);
      })
      .catch(() => {
        alert("Failed to update home delivery status");
      });
  };

  const searchOrders = useCallback(
    async (value) => {
      const trimmed = value.trim();

      if (!trimmed) {
        setIsSearching(false);
        setMessage("");
        setPage(1);
        loadOrders(1, false);
        return;
      }

      try {
        setLoading(true);
        setIsSearching(true);
        setMessage("");

        const response = await fetch(
          `${backendURI}/api/orders/findbyname?name=${encodeURIComponent(
            trimmed
          )}&owner_id=${conditions.id}&hd=${conditions.home_delivery}&limit=100`
        );
        const json = await response.json();

        const items = Array.isArray(json.items) ? json.items : [];

        if (!items.length) {
          setData([]);
          setMessage("No orders found by this customer name");
          setHasMore(false);
          return;
        }

        setData(items);
        setHasMore(false);
      } catch (err) {
        setData([]);
        setMessage("Search failed");
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [backendURI, conditions.id, conditions.home_delivery, loadOrders]
  );

  const custOrderFind = (e) => {
    const value = e.target.value;
    setSearchText(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchOrders(value);
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const loadMore = async () => {
    const nextPage = page + 1;
    setPage(nextPage);
    await loadOrders(nextPage, true);
  };

  const formatOrderDate = (dateValue) => {
    const date = new Date(Date.parse(dateValue));
    if (Number.isNaN(date.getTime())) return "-";

    return (
      <>
        {date.toDateString()}
        <br />
        {date.toLocaleTimeString()}
      </>
    );
  };

  const renderRows = () => {
    if (loading && data.length === 0) {
      return (
        <tr>
          <td colSpan="13" className="text-center">
            Loading...
          </td>
        </tr>
      );
    }

    if (message) {
      return (
        <tr>
          <td colSpan="13" className="text-center text-danger">
            {message}
          </td>
        </tr>
      );
    }

    if (!data.length) {
      return (
        <tr>
          <td colSpan="13" className="text-center">
            No data
          </td>
        </tr>
      );
    }

    return data.map((item) => (
      <tr
        className={
          (item.paid === true ? "bg-success " : "") + "my-2 mx-2 text-center"
        }
        key={item.id}
      >
        <th>
          <Button variant="danger" onClick={() => handleDelete(item.id)}>
            Del
          </Button>
        </th>
        <th>{item.owner_id}</th>
        <th>{item.id}</th>
        <th>{item.customer_name}</th>
        <th>{item.nos}</th>
        <th>{formatOrderDate(item.date_of_order)}</th>
        <th>{item.total}</th>
        <th>{item.advance}</th>
        <th>{item.balence}</th>
        <th>
          <Form.Check
            id={`processing-${item.id}`}
            value="process"
            checked={Boolean(item.process)}
            onChange={(e) => handleChange(e, item.id, item.customer_name)}
            type="checkbox"
            size="lg"
          />
        </th>
        <th>
          <Form.Check
            id={`complete-${item.id}`}
            value="completed"
            checked={Boolean(item.completed)}
            onChange={(e) => handleChange(e, item.id, item.customer_name)}
            type="checkbox"
            size="lg"
          />
        </th>
        <th>
          <Form.Check
            id={`paid-${item.id}`}
            value="paid"
            checked={Boolean(item.paid)}
            onChange={(e) => handleChange(e, item.id, item.customer_name)}
            type="checkbox"
            size="lg"
          />
        </th>
        <th>
          <Button
            onClick={() =>
              navigate("/details", {
                state: {
                  id: item.id,
                  name: item.customer_name,
                },
              })
            }
            className="mx-2 my-2"
            variant="warning"
            size="lg"
          >
            &#9432;
          </Button>
        </th>
      </tr>
    ));
  };

  return (
    <div className="bg-light mt-3">
      <Header />
      <br />

      <h3 className="text-danger mx-4">Shop: {conditions.shopname}</h3>

      <Row>
        <Col>
          <h4 className="text-primary mx-4">Admin: {conditions.username}</h4>
        </Col>
        <Col>
          <span> </span>
        </Col>
        <Col>
          <Form.Check
            checked={conditions.pk_stat}
            value=""
            onChange={handleHd}
            label="Home delivery on/off"
          />
        </Col>
      </Row>

      <br />

      <div className="my-3 d-flex flex-row justify-content-center">
        <Button
          onClick={() => navigate("/neworders")}
          className="col mx-4 my-2"
          variant="outline-info"
        >
          Add New Order +
        </Button>

        <Button
          onClick={() => navigate("/newcustomer")}
          className="col mx-4 my-2"
          variant="outline-info"
        >
          Add New Customer +
        </Button>

        <Button
          onClick={() => navigate("/employeedash")}
          className="col mx-4 my-2"
          variant="outline-info"
        >
          Pickup Orders
        </Button>

        <Button
          onClick={() => navigate("/tally")}
          className="col mx-4 my-2"
          variant="outline-info"
        >
          Tally
        </Button>
      </div>

      <hr />
      <br />

      <div className="my-3 mx-3">
        <Row>
          <Col>
            <Form.Group>
              <Form.Label className="text-dark fs-4 text-decoration-underline mx-5">
                Find orders by customer name
              </Form.Label>
              <Form.Control
                type="text"
                variant="outline-info"
                value={searchText}
                onChange={custOrderFind}
                placeholder="Search customer name"
              />
            </Form.Group>
          </Col>
          <Col>
            {message ? <h3 className="mt-4 text-danger">{message}</h3> : ""}
          </Col>
        </Row>
      </div>

      <Tabs
        defaultActiveKey="no"
        onSelect={(key) => {
          setConditions((prev) => ({
            ...prev,
            home_delivery: key === "yes",
          }));
        }}
      >
        <Tab eventKey="no" title="Counter Orders">
          <div>
            <Table responsive striped bordered hover>
              <thead>
                <tr className="text-center">
                  <th></th>
                  <th>OwnerId</th>
                  <th>OrderId</th>
                  <th>Customer Name</th>
                  <th>NOS</th>
                  <th>Date Of Order</th>
                  <th>Total</th>
                  <th>Advance</th>
                  <th>Balence</th>
                  <th>Processing</th>
                  <th>Completed</th>
                  <th>Paid</th>
                  <th>View Details</th>
                </tr>
              </thead>
              <tbody>{renderRows()}</tbody>
            </Table>
          </div>
        </Tab>

        <Tab eventKey="yes" title="Home Delivery Orders">
          <div>
            <Table responsive striped bordered hover>
              <thead>
                <tr className="text-center">
                  <th></th>
                  <th>OwnerId</th>
                  <th>OrderId</th>
                  <th>Customer Name</th>
                  <th>NOS</th>
                  <th>Date Of Order</th>
                  <th>Total</th>
                  <th>Advance</th>
                  <th>Balence</th>
                  <th>Processing</th>
                  <th>Completed</th>
                  <th>Paid</th>
                  <th>View Details</th>
                </tr>
              </thead>
              <tbody>{renderRows()}</tbody>
            </Table>
          </div>
        </Tab>
      </Tabs>

      {!isSearching && hasMore && data.length > 0 && (
        <div className="d-flex justify-content-center my-4">
          <Button onClick={loadMore} disabled={loading} variant="outline-primary">
            {loading ? "Loading..." : "Load More"}
          </Button>
        </div>
      )}
    </div>
  );
}

export default Orders;
